import { Pipe, PipeTransform } from '@angular/core';
import { environment } from '../../../environments/environment';
import { formatFileSize } from '../file-size.util';

/**
 * Formats a byte count for display, always in decimal (SI): divide by 1,000,000
 * for MB, and label it `MB`. Never `MiB`.
 *
 * Until 2026-07-10 this pipe took a second `base` argument (1000 or 1024) chosen
 * by `FileSizeModeService` from the runtime-config `fileSizeValidationMode` flag,
 * mirroring a backend that sniffed `User-Agent` to pick a byte-unit validator.
 * The backend removed that behaviour — every size it reports is decimal on every
 * client — so the mode, the service, and the argument were all deleted. Do not
 * reintroduce a base parameter.
 *
 * Our "MB" will disagree with Windows File Explorer's "MB" for the same file, by
 * design. See the header of `file-size.util.ts` for why, and for the accepted
 * round-to-nearest limitation near the cap boundary.
 *
 * Precision comes from the build-time `environment.fileSizeDecimalPrecision`
 * (1 everywhere today, so "49.5 MB"). Whole numbers drop their trailing ".0",
 * so a 50,000,000-byte file reads "50 MB".
 *
 * `pure` (the default) is now unconditionally safe: the pipe reads no signals and
 * has no injected state, so its output depends only on its input.
 */
@Pipe({
  name: 'fileSize',
})
export class FileSizePipe implements PipeTransform {
  transform(bytes: number | null | undefined): string {
    return formatFileSize(bytes, environment.fileSizeDecimalPrecision);
  }
}
